import {
  createFileRoute,
  Link,
  useCanGoBack,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ArrowLeft, ArrowRight, Download, DownloadCloud, FileX, Loader2, Pencil, RefreshCw, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { PageHeader } from "@/components/layout/page-header";
import { RefreshButton } from "@/components/data/refresh-button";
import { EditGrantorDialog } from "../-dialogs/edit-grantor-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useRugLoginValid } from "@/components/rug/rug-login-guard";
import { useAuth } from "@/auth/auth-provider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { EmptyState } from "@/components/data/empty-state";
import { DetailField as Field } from "@/components/data/detail-field";
import {
  ExecutionItemStatusBadge,
  RegistrationStatusBadge,
} from "@/components/data/status-badge";
import { registrationsApi } from "@/api/registrations";
import { receiptsApi } from "@/api/receipts";
import { executionsApi, type ExecutionItem } from "@/api/executions";
import { queryKeys } from "@/api/query-keys";
import { ApiError } from "@/api/client";
import { authErrorMessage } from "@/auth/errors";
import { requireRole } from "@/auth/role-guard";
import { formatDateTime, formatRelative, localeFor } from "@/lib/format";
import { History } from "lucide-react";

export const Route = createFileRoute("/_authenticated/registrations/$registrationId/")({
  beforeLoad: requireRole(["admin", "inscripcion"]),
  component: RegistrationDetailPage,
});

function RegistrationDetailPage() {
  const { registrationId } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const { t } = useTranslation();

  // Volver por el historial conserva los filtros de la lista (viven en su URL).
  // Fallback (entrada directa al detalle, sin historial): al listado sin filtros.
  const goBackToList = () =>
    canGoBack ? router.history.back() : navigate({ to: "/registrations" });

  const reg = useQuery({
    queryKey: queryKeys.registrations.detail(registrationId),
    queryFn: () => registrationsApi.get(registrationId),
  });

  const timeline = useQuery({
    queryKey: queryKeys.registrations.timeline(registrationId),
    queryFn: () => executionsApi.listItemsByRegistration(registrationId),
  });

  const { isValid: loginValid } = useRugLoginValid("inscripcion");
  const { user } = useAuth();

  // Boleta ya descargada: la localizamos por registrationId para ofrecer la
  // descarga directa del PDF (URL prefirmada) sin salir del detalle.
  const boletaReceipts = useQuery({
    queryKey: queryKeys.receipts.list({ registrationId }),
    queryFn: () => receiptsApi.list({ registrationId }),
    enabled: !!reg.data?.boletaS3Key,
  });
  const boletaReceipt =
    boletaReceipts.data?.items.find(
      (r) => r.s3Key === reg.data?.boletaS3Key,
    ) ?? boletaReceipts.data?.items[0];
  const [redownloadOpen, setRedownloadOpen] = useState(false);
  const [deregisterOpen, setDeregisterOpen] = useState(false);
  const [reinscribirOpen, setReinscribirOpen] = useState(false);
  // Segunda confirmación del diálogo: sin marcarla no se puede confirmar.
  const [reinscribirVerificado, setReinscribirVerificado] = useState(false);

  const retryMutation = useMutation({
    mutationFn: () => registrationsApi.retry(registrationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.registrations.all });
      toast.success(t("registrations.detail.retryToast"));
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: () => registrationsApi.remove(registrationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.registrations.all });
      toast.success(t("registrations.detail.deleteToast"));
      goBackToList();
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });
  const redownloadMutation = useMutation({
    mutationFn: () => registrationsApi.redownloadBoleta(registrationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.registrations.all });
      qc.invalidateQueries({ queryKey: queryKeys.receipts.all });
      toast.success(t("registrations.detail.redownloadBoletaToast"));
      setRedownloadOpen(false);
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });
  // Re-inscripción: el backend reinicia el MISMO registro a la cola para volver a firmar.
  const reinscribirMutation = useMutation({
    mutationFn: () => registrationsApi.reinscribir(registrationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.registrations.all });
      toast.success(t("registrations.detail.reinscribirToast"));
      setReinscribirOpen(false);
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });
  // Baja manual: crea el registro de baja y abre (o reutiliza) la ventana de
  // debounce de 10 min. NO marca la inscripción: eso lo hace el resultado del RUG.
  const deregisterMutation = useMutation({
    mutationFn: () => registrationsApi.deregister(registrationId),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: queryKeys.registrations.all });
      qc.invalidateQueries({ queryKey: queryKeys.bajas.all });
      // Dedup: ya existía una baja activa para esta garantía que no nació de aquí
      // (típicamente de un layout). No se ligó nada, así que el toast lleva el enlace:
      // es la ÚNICA pista que le queda al operador para encontrar esa baja.
      if (res.alreadyInProgress) {
        toast.warning(t("registrations.detail.deregisterAlreadyToast"), {
          action: {
            label: t("registrations.detail.deregisterAlreadyLink"),
            onClick: () =>
              navigate({
                to: "/bajas/$bajaId",
                params: { bajaId: res.deregistrationId },
              }),
          },
        });
      } else {
        toast.success(t("registrations.detail.deregisterToast"));
      }
      setDeregisterOpen(false);
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });
  // Descarga del PDF ya existente en S3 (distinta de redownload, que re-encola
  // al bot). Abre la URL prefirmada en una pestaña nueva.
  const downloadBoletaMutation = useMutation({
    mutationFn: (id: string) => receiptsApi.download(id),
    onSuccess: (info) => {
      window.open(info.url, "_blank", "noopener,noreferrer");
    },
    onError: (err) => {
      // El registro existe pero el PDF aún no está en S3 → 404: mensaje claro.
      if (err instanceof ApiError && err.status === 404) {
        toast.error(t("receipts.unavailable"));
      } else {
        toast.error(authErrorMessage(err));
      }
    },
  });

  // Solo se borra lo que nunca firmó (el backend re-valida). Bloqueado: en vuelo + post-firma.
  // Incluye "failed" (legacy) = mismos semánticos que signed_failed.
  const canDelete = ["signed_failed", "failed", "pending", "scheduled"].includes(
    reg.data?.status ?? "",
  );
  // Re-descarga de boleta (Objetivo B): solo estados post-firma (la inscripción
  // legal ya existe en el RUG). Excluye signed_failed y pre-firma.
  // Con una baja en trámite NO se re-procesa la boleta: el reset borra números y
  // boleta, y si la cancelación se confirma después la inscripción quedaría
  // `deregistered` sin el histórico que debe conservar. El backend lo re-valida.
  const canRedownloadBoleta =
    ["signed", "data_extracted", "downloaded", "download_failed"].includes(
      reg.data?.status ?? "",
    ) && !reg.data?.deregistrationId;
  // Baja manual. El gate ESPEJA al backend (`planDeregistration`), no solo el status:
  // sin VIN o sin número de garantía el servidor rechaza, y ofrecer el botón para que
  // falle al confirmar sería mentirle al operador. Además exige rol (admin/baja) y
  // login RUG válido del flujo `baja`, no del de inscripción.
  const { isValid: bajaLoginValid } = useRugLoginValid("baja");
  const groups = user?.groups ?? [];
  // Espeja al authorizer del gateway: `admin`, o bien alguien que tenga `inscripcion`
  // (para poder leer el detalle) Y `baja` (para el POST de la cancelación). Un usuario
  // con SOLO el rol `baja` no puede entrar a esta ruta, así que ofrecerle el botón
  // sería mentirle. Decisión del 2026-07-30: en la práctica la baja manual la hace admin.
  const canDeregisterRole =
    groups.includes("admin") ||
    (groups.includes("inscripcion") && groups.includes("baja"));
  const canDeregister =
    reg.data?.status === "downloaded" &&
    !!reg.data?.guarantee?.vin?.trim() &&
    !!reg.data?.numeroGarantia?.trim() &&
    canDeregisterRole;
  // Re-inscribir: solo admin (el authorizer la niega a los demás) y solo si el backend
  // dice que procede. La regla NO se repite aquí: `reinscribible` ya la trae evaluada.
  const canReinscribir = groups.includes("admin") && !!reg.data?.reinscribible;
  // Editar: `signed_failed` PF corrige otorgante + VIN (vuelve a firmar); en una ya
  // firmada solo el VIN, cuando el backend dice que procede (`vinCorregible`, que
  // incluye `signed` con búsqueda "sin resultados", no solo `download_failed`).
  const canEditGrantor =
    reg.data?.status === "signed_failed" && reg.data.grantors?.[0]?.personType === "PF";
  const canEditVin = !!reg.data?.vinCorregible;
  // Baja SOLICITADA y aún sin confirmar por el RUG: hay vínculo pero el status sigue
  // siendo el de la inscripción viva.
  const deregisterPending =
    !!reg.data?.deregistrationId && reg.data.status !== "deregistered";
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={goBackToList}
        className="inline-flex cursor-pointer items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="size-4" />
        {t("registrations.detail.back")}
      </button>

      {reg.isLoading ? (
        <DetailSkeleton />
      ) : reg.isError ? (
        <ErrorBanner error={reg.error} message={t("registrations.detail.loadError")} />
      ) : reg.data ? (
        <>
          <PageHeader
            title={reg.data.contractNumber}
            subtitle={
              <span className="inline-flex items-center gap-2">
                <RegistrationStatusBadge status={reg.data.status} />
                {/* Baja solicitada, pendiente de que el RUG la confirme. El status
                    sigue siendo el de la inscripción viva a propósito. */}
                {deregisterPending ? (
                  <Link
                    to="/bajas/$bajaId"
                    params={{ bajaId: reg.data.deregistrationId as string }}
                    className="inline-flex items-center gap-1 rounded-full border border-warning/30 bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning-foreground hover:bg-warning/20"
                  >
                    <FileX className="size-3" />
                    {t("registrations.detail.deregisterPending")}
                  </Link>
                ) : null}
                {/* La última baja no se aplicó. Aviso propio, NO el banner de error de
                    la inscripción: la inscripción está sana. */}
                {reg.data.lastDeregistrationError && !deregisterPending ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border border-warning/30 bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning-foreground"
                    title={reg.data.lastDeregistrationError}
                  >
                    <AlertCircle className="size-3" />
                    {t("registrations.detail.deregisterFailed")}
                  </span>
                ) : null}
                {reg.data.status === "deregistered" && reg.data.deregistrationId ? (
                  <Link
                    to="/bajas/$bajaId"
                    params={{ bajaId: reg.data.deregistrationId }}
                    className="text-xs underline text-muted-foreground hover:text-foreground"
                  >
                    {t("registrations.detail.deregisterLink")}
                  </Link>
                ) : null}
                {reg.data.scheduledFor && reg.data.status === "scheduled" ? (
                  <span className="text-muted-foreground">
                    {t("registrations.detail.scheduledFor")}{" "}
                    <span className="text-foreground">
                      {new Date(reg.data.scheduledFor).toLocaleString(localeFor(), {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </span>
                  </span>
                ) : reg.data.status === "queued" ? (
                  <span className="text-muted-foreground">
                    {t("registrations.detail.queuedShort")}
                  </span>
                ) : null}
                {reg.data.folioRug ? (
                  <span className="text-muted-foreground">
                    {t("registrations.detail.folioLabel")}{" "}
                    <span className="font-mono text-foreground">
                      {reg.data.folioRug}
                    </span>
                  </span>
                ) : null}
                {reg.data.numeroGarantia ? (
                  <span className="text-muted-foreground">
                    {t("registrations.detail.guaranteeNumber")}{" "}
                    <span className="font-mono text-foreground">
                      {reg.data.numeroGarantia}
                    </span>
                    {reg.data.numeroOperacion ? (
                      <>
                        {" · "}
                        {t("registrations.detail.operation")}{" "}
                        <span className="font-mono text-foreground">
                          {reg.data.numeroOperacion}
                        </span>
                      </>
                    ) : null}
                  </span>
                ) : null}
              </span>
            }
            actions={
              <div className="flex items-center gap-2">
                <RefreshButton
                  onClick={() => {
                    reg.refetch();
                    timeline.refetch();
                  }}
                  spinning={reg.isFetching || timeline.isFetching}
                />
                {canEditGrantor || canEditVin ? (
                  <Button variant="outline" onClick={() => setEditOpen(true)}>
                    <Pencil className="size-4" />
                    {t("registrations.actions.edit")}
                  </Button>
                ) : null}
                {reg.data.status === "signed_failed" ? (
                  <Button
                    disabled={retryMutation.isPending || !loginValid}
                    onClick={() => retryMutation.mutate()}
                  >
                    <RefreshCw
                      className={
                        retryMutation.isPending ? "size-4 animate-spin" : "size-4"
                      }
                    />
                    {t("registrations.actions.retry")}
                  </Button>
                ) : null}
                {reg.data.boletaS3Key ? (
                  <Button
                    variant="outline"
                    disabled={!boletaReceipt || downloadBoletaMutation.isPending}
                    onClick={() =>
                      boletaReceipt &&
                      downloadBoletaMutation.mutate(boletaReceipt.id)
                    }
                  >
                    {downloadBoletaMutation.isPending ||
                    boletaReceipts.isLoading ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Download className="size-4" />
                    )}
                    {t("registrations.detail.downloadReceipt")}
                  </Button>
                ) : null}
                {canRedownloadBoleta ? (
                  <Button
                    variant="outline"
                    disabled={redownloadMutation.isPending}
                    onClick={() => setRedownloadOpen(true)}
                  >
                    {redownloadMutation.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <DownloadCloud className="size-4" />
                    )}
                    {t("registrations.actions.redownloadBoleta")}
                  </Button>
                ) : null}
                {canReinscribir ? (
                  <Button
                    variant="outline"
                    className="text-destructive hover:bg-destructive/10"
                    disabled={reinscribirMutation.isPending || !loginValid}
                    onClick={() => {
                      // La casilla se limpia en cada apertura: una confirmación vieja no
                      // vale para una decisión nueva.
                      setReinscribirVerificado(false);
                      setReinscribirOpen(true);
                    }}
                  >
                    {reinscribirMutation.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <RotateCcw className="size-4" />
                    )}
                    {t("registrations.actions.reinscribir")}
                  </Button>
                ) : null}
                {canDeregister ? (
                  <Button
                    variant="outline"
                    className="text-destructive hover:bg-destructive/10"
                    disabled={
                      deregisterMutation.isPending ||
                      deregisterPending ||
                      !bajaLoginValid
                    }
                    onClick={() => setDeregisterOpen(true)}
                  >
                    {deregisterMutation.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <FileX className="size-4" />
                    )}
                    {t("registrations.actions.deregister")}
                  </Button>
                ) : null}
                {canDelete ? (
                  <Button
                    variant="outline"
                    className="text-destructive hover:bg-destructive/10"
                    disabled={deleteMutation.isPending}
                    onClick={() => setDeleteOpen(true)}
                  >
                    {deleteMutation.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Trash2 className="size-4" />
                    )}
                    {t("registrations.actions.delete")}
                  </Button>
                ) : null}
              </div>
            }
          />

          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("registrations.detail.info.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    {t("registrations.detail.parties.grantor")}
                  </p>
                  <p className="font-medium truncate" title={reg.data.grantorName}>
                    {reg.data.grantorName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {reg.data.grantorPersonType === "PM"
                      ? t("registrations.detail.info.personMoral")
                      : t("registrations.detail.info.personFisica")}
                  </p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    {t("registrations.detail.parties.creditor")}
                  </p>
                  <p className="font-medium truncate">{reg.data.principalCreditor}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    {t("registrations.detail.info.guaranteeType")}
                  </p>
                  <p className="font-medium">
                    {reg.data.guarantee?.guaranteeType ??
                      reg.data.guarantee?.assetTypes?.[0] ??
                      "—"}
                  </p>
                </div>
              </div>

              {reg.data.guarantee?.assetsDescription ? (
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t("registrations.detail.info.asset")}
                  </p>
                  <p className="text-sm text-foreground">
                    {reg.data.guarantee.assetsDescription}
                  </p>
                </div>
              ) : null}

              <Separator />

              <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
                <Field label={t("registrations.detail.info.contractType")} value={reg.data.contractType} />
                <Field label={t("registrations.detail.info.vin")} value={reg.data.guarantee?.vin} mono />
                <Field
                  label={t("registrations.detail.info.maxAmount")}
                  value={
                    reg.data.guarantee?.maxAmount != null
                      ? new Intl.NumberFormat(localeFor(), {
                          style: "currency",
                          currency: reg.data.guarantee.currency ?? "MXN",
                        }).format(reg.data.guarantee.maxAmount)
                      : undefined
                  }
                />
                <Field label={t("registrations.detail.info.currency")} value={reg.data.guarantee?.currency} />
                <Field
                  label={t("registrations.detail.info.term")}
                  value={
                    reg.data.registrationTermMonths != null
                      ? t("registrations.detail.info.months", { count: reg.data.registrationTermMonths })
                      : undefined
                  }
                />
                <Field label={t("registrations.detail.info.signingDate")} value={reg.data.guarantee?.signingDate} />
                <Field label={t("registrations.detail.info.terminationDate")} value={reg.data.obligation?.terminationDate} />
                <Field label={t("registrations.detail.info.contractActType")} value={reg.data.obligation?.contractActType} />
                <Field label={t("registrations.detail.info.rfc")} value={reg.data.grantorRfc} mono />
                <Field label={t("registrations.detail.info.curp")} value={reg.data.grantors?.[0]?.curp} mono />
                <Field label={t("registrations.detail.info.folio")} value={reg.data.grantors?.[0]?.electronicFolio} mono />
                <Field label={t("registrations.detail.info.attempts")} value={String(reg.data.attempts ?? 0)} />
                <Field label={t("registrations.detail.info.createdAt")} value={formatDateTime(reg.data.createdAt)} />
                <Field label={t("registrations.detail.info.updatedAt")} value={formatDateTime(reg.data.updatedAt)} />
              </dl>
            </CardContent>
          </Card>

          {reg.data.errorMessage ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive flex items-start gap-3 mb-6">
              <AlertCircle className="size-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">{t("registrations.detail.lastError")}</p>
                <p className="text-xs mt-0.5 text-destructive/80">
                  {reg.data.errorMessage}
                </p>
              </div>
            </div>
          ) : null}

          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="text-sm font-medium">
                {t("registrations.detail.historyTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {timeline.isLoading ? (
                <TimelineSkeleton />
              ) : timeline.isError ? (
                <ErrorBanner
                  error={timeline.error}
                  message={t("registrations.detail.historyLoadError")}
                />
              ) : timeline.data && timeline.data.items.length > 0 ? (
                <ol className="relative border-l border-border pl-6 space-y-5">
                  {timeline.data.items.map((item) => (
                    <TimelineRow key={item.executionId} item={item} />
                  ))}
                </ol>
              ) : (
                <EmptyState
                  icon={History}
                  title={t("registrations.detail.historyEmptyTitle")}
                  description={t("registrations.detail.historyEmptyDescription")}
                  className="py-8"
                />
              )}
            </CardContent>
          </Card>

          <EditGrantorDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            registrationId={registrationId}
            vinOnly={canEditVin}
            grantor={reg.data.grantors?.[0]}
            vin={reg.data.guarantee?.vin}
          />

          <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("registrations.detail.deleteTitle")}</DialogTitle>
                <DialogDescription>
                  {t("registrations.detail.deleteConfirm")}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" disabled={deleteMutation.isPending}>
                    {t("common.actions.cancel")}
                  </Button>
                </DialogClose>
                <Button
                  variant="destructive"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate()}
                >
                  {deleteMutation.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Trash2 className="size-4" />
                  )}
                  {t("registrations.actions.delete")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={redownloadOpen} onOpenChange={setRedownloadOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {t("registrations.detail.redownloadTitle")}
                </DialogTitle>
                <DialogDescription>
                  {t("registrations.detail.redownloadConfirm")}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button
                    variant="outline"
                    disabled={redownloadMutation.isPending}
                  >
                    {t("common.actions.cancel")}
                  </Button>
                </DialogClose>
                <Button
                  disabled={redownloadMutation.isPending}
                  onClick={() => redownloadMutation.mutate()}
                >
                  {redownloadMutation.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <DownloadCloud className="size-4" />
                  )}
                  {t("registrations.actions.redownloadBoleta")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={reinscribirOpen} onOpenChange={setReinscribirOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {t("registrations.detail.reinscribirTitle")}
                </DialogTitle>
                <DialogDescription>
                  {t("registrations.detail.reinscribirConfirm")}
                </DialogDescription>
              </DialogHeader>
              {/* "Sin resultados" también sale con un VIN mal escrito, y ahí re-firmar
                  duplica una garantía que sí existe (MSN20250930169, 2026-10-13). */}
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400 flex items-start gap-2">
                <AlertCircle className="size-4 mt-0.5 shrink-0" />
                <p>
                  {t("registrations.detail.reinscribirVinWarning", {
                    vin: reg.data.guarantee?.vin || "-",
                  })}
                </p>
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox
                  className="mt-0.5"
                  checked={reinscribirVerificado}
                  onCheckedChange={setReinscribirVerificado}
                  disabled={reinscribirMutation.isPending}
                />
                <span>{t("registrations.detail.reinscribirCheck")}</span>
              </label>
              <DialogFooter>
                <DialogClose asChild>
                  <Button
                    variant="outline"
                    disabled={reinscribirMutation.isPending}
                  >
                    {t("common.actions.cancel")}
                  </Button>
                </DialogClose>
                <Button
                  variant="destructive"
                  disabled={!reinscribirVerificado || reinscribirMutation.isPending}
                  onClick={() => reinscribirMutation.mutate()}
                >
                  {reinscribirMutation.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <RotateCcw className="size-4" />
                  )}
                  {t("registrations.actions.reinscribir")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={deregisterOpen} onOpenChange={setDeregisterOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {t("registrations.detail.deregisterTitle")}
                </DialogTitle>
                <DialogDescription>
                  {t("registrations.detail.deregisterConfirm")}
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
                <p>
                  <span className="text-muted-foreground">
                    {t("registrations.columns.contract")}:{" "}
                  </span>
                  {reg.data.contractNumber}
                </p>
                <p>
                  <span className="text-muted-foreground">
                    {t("registrations.detail.guaranteeNumber")}:{" "}
                  </span>
                  {reg.data.numeroGarantia ?? "—"}
                </p>
                <p>
                  <span className="text-muted-foreground">
                    {t("registrations.detail.info.vin")}:{" "}
                  </span>
                  <span className="font-mono text-xs">
                    {reg.data.guarantee?.vin ?? "—"}
                  </span>
                </p>
                <p>
                  <span className="text-muted-foreground">
                    {t("registrations.columns.grantor")}:{" "}
                  </span>
                  {reg.data.grantorName}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("registrations.detail.deregisterNote")}
              </p>
              <DialogFooter>
                <DialogClose asChild>
                  <Button
                    variant="outline"
                    disabled={deregisterMutation.isPending}
                  >
                    {t("common.actions.cancel")}
                  </Button>
                </DialogClose>
                <Button
                  variant="destructive"
                  disabled={deregisterMutation.isPending}
                  onClick={() => deregisterMutation.mutate()}
                >
                  {deregisterMutation.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <FileX className="size-4" />
                  )}
                  {t("registrations.actions.deregister")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      ) : null}
    </>
  );
}

function TimelineRow({ item }: { item: ExecutionItem }) {
  const { t } = useTranslation();
  const dotTone =
    item.status === "success"
      ? "bg-success"
      : item.status === "failed"
        ? "bg-destructive"
        : item.status === "skipped_duplicate"
          ? "bg-warning"
          : "bg-primary";

  return (
    <li className="relative">
      <span
        className={`absolute -left-[31px] top-1 size-3 rounded-full ring-4 ring-background ${dotTone}`}
      />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <ExecutionItemStatusBadge status={item.status} />
            <span className="text-sm">
              {formatDateTime(item.endedAt ?? item.startedAt)}
            </span>
            <span className="text-xs text-muted-foreground">
              · {formatRelative(item.endedAt ?? item.startedAt)}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("registrations.detail.internalAttempts", {
              count: item.attemptsInExecution,
            })}
            {item.folioRug ? (
              <>
                {" · "}
                {t("registrations.columns.folio", { folio: "" })}
                <span className="font-mono text-foreground">
                  {item.folioRug}
                </span>
              </>
            ) : null}
          </p>
          {item.errorMessage ? (
            <p className="text-xs text-destructive max-w-xl">
              {item.errorMessage}
            </p>
          ) : null}
        </div>
        <Link
          to="/executions/$executionId"
          params={{ executionId: item.executionId }}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline whitespace-nowrap"
        >
          {t("registrations.detail.viewExecution")}
          <ArrowRight className="size-3" />
        </Link>
      </div>
    </li>
  );
}

function DetailSkeleton() {
  return (
    <>
      <Skeleton className="h-8 w-72 mb-2" />
      <Skeleton className="h-4 w-48 mb-6" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="h-fit">
            <CardHeader className="pb-2">
              <Skeleton className="h-3 w-24" />
            </CardHeader>
            <CardContent className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-3 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}

function TimelineSkeleton() {
  return (
    <ol className="relative border-l border-border pl-6 space-y-5">
      {Array.from({ length: 3 }).map((_, i) => (
        <li key={i} className="relative">
          <span className="absolute -left-[31px] top-1 size-3 rounded-full ring-4 ring-background bg-muted" />
          <Skeleton className="h-4 w-48 mb-1.5" />
          <Skeleton className="h-3 w-32" />
        </li>
      ))}
    </ol>
  );
}

function ErrorBanner({ error, message }: { error: unknown; message: string }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive flex items-start gap-3">
      <AlertCircle className="size-4 mt-0.5 shrink-0" />
      <div>
        <p className="font-medium">{message}</p>
        <p className="text-xs mt-0.5 text-destructive/80">
          {error instanceof Error ? error.message : t("common.error.retryHint")}
        </p>
      </div>
    </div>
  );
}
